<template>
  <div class="holidays-page">
    <div class="page-header">
      <h1 class="page-title">节假日查询</h1>
      <p class="page-desc">查询各国法定节假日</p>
    </div>

    <!-- 选择 -->
    <div class="select-row">
      <div class="select-group">
        <label>国家/地区</label>
        <select v-model="selectedCountry" class="select-input" @change="loadHolidays">
          <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.flag }} {{ c.name }}</option>
        </select>
      </div>
      <div class="select-group">
        <label>年份</label>
        <select v-model="selectedYear" class="select-input short" @change="loadHolidays">
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}年</option>
        </select>
      </div>
    </div>

    <!-- 节假日列表 -->
    <div class="holiday-list" v-if="holidays.length">
      <div class="holiday-count">共 {{ holidays.length }} 个节假日</div>
      <div
        v-for="(h, i) in holidays"
        :key="i"
        class="holiday-card"
        :class="{ 'is-past': isPast(h.date) }"
      >
        <div class="h-date">
          <span class="h-month">{{ formatMonth(h.date) }}</span>
          <span class="h-day">{{ formatDay(h.date) }}</span>
        </div>
        <div class="h-info">
          <div class="h-name">{{ h.name }}</div>
          <div class="h-desc" v-if="h.desc">{{ h.desc }}</div>
        </div>
        <div class="h-badge" v-if="isToday(h.date)">今天</div>
        <div class="h-badge upcoming" v-else-if="isUpcoming(h.date)">即将到来</div>
      </div>
    </div>

    <div v-else-if="!loading" class="empty-hint">暂无该年份的节假日数据</div>

    <!-- 常见节假日说明 -->
    <div class="help-block">
      <h3>中国法定节假日</h3>
      <ul>
        <li><strong>元旦</strong>：1月1日，放假1天</li>
        <li><strong>春节</strong>：农历正月初一，放假3天（通常连休7天）</li>
        <li><strong>清明节</strong>：农历清明当日，放假1天</li>
        <li><strong>劳动节</strong>：5月1日，放假1天（通常连休5天）</li>
        <li><strong>端午节</strong>：农历五月初五，放假1天</li>
        <li><strong>中秋节</strong>：农历八月十五，放假1天</li>
        <li><strong>国庆节</strong>：10月1日~10月3日，放假3天（通常连休7天）</li>
      </ul>
    </div>
  </div>
</template>

<script>
// 内置中国节假日数据（2025-2028）
const CHINA_HOLIDAYS = {
  2025: [
    { date: '2025-01-01', name: '元旦', desc: '新年第一天' },
    { date: '2025-01-28', name: '除夕', desc: '农历腊月三十' },
    { date: '2025-01-29', name: '春节', desc: '农历正月初一' },
    { date: '2025-01-30', name: '春节假期', desc: '农历正月初二' },
    { date: '2025-01-31', name: '春节假期', desc: '农历正月初三' },
    { date: '2025-04-04', name: '清明节', desc: '踏青祭祖' },
    { date: '2025-05-01', name: '劳动节', desc: '国际劳动节' },
    { date: '2025-05-31', name: '端午节', desc: '农历五月初五' },
    { date: '2025-10-01', name: '国庆节', desc: '中华人民共和国成立纪念日' },
    { date: '2025-10-02', name: '国庆假期', desc: '' },
    { date: '2025-10-03', name: '国庆假期', desc: '' },
    { date: '2025-10-06', name: '中秋节', desc: '农历八月十五' },
    { date: '2025-02-14', name: '情人节', desc: '非官方节日' },
    { date: '2025-03-08', name: '妇女节', desc: '国际劳动妇女节' },
    { date: '2025-06-01', name: '儿童节', desc: '国际儿童节' },
    { date: '2025-09-10', name: '教师节', desc: '' },
    { date: '2025-12-25', name: '圣诞节', desc: '非官方节日' }
  ],
  2026: [
    { date: '2026-01-01', name: '元旦', desc: '新年第一天' },
    { date: '2026-02-16', name: '除夕', desc: '农历腊月三十' },
    { date: '2026-02-17', name: '春节', desc: '农历正月初一' },
    { date: '2026-02-18', name: '春节假期', desc: '农历正月初二' },
    { date: '2026-02-19', name: '春节假期', desc: '农历正月初三' },
    { date: '2026-04-05', name: '清明节', desc: '踏青祭祖' },
    { date: '2026-05-01', name: '劳动节', desc: '国际劳动节' },
    { date: '2026-06-19', name: '端午节', desc: '农历五月初五' },
    { date: '2026-09-25', name: '中秋节', desc: '农历八月十五' },
    { date: '2026-10-01', name: '国庆节', desc: '中华人民共和国成立纪念日' },
    { date: '2026-10-02', name: '国庆假期', desc: '' },
    { date: '2026-10-03', name: '国庆假期', desc: '' },
    { date: '2026-02-14', name: '情人节', desc: '非官方节日' },
    { date: '2026-03-08', name: '妇女节', desc: '' },
    { date: '2026-06-01', name: '儿童节', desc: '' },
    { date: '2026-09-10', name: '教师节', desc: '' },
    { date: '2026-12-25', name: '圣诞节', desc: '非官方节日' }
  ]
}

// 美国节假日
const US_HOLIDAYS = {
  2026: [
    { date: '2026-01-01', name: 'New Year\'s Day', desc: '新年' },
    { date: '2026-01-19', name: 'Martin Luther King Jr. Day', desc: '马丁·路德·金纪念日' },
    { date: '2026-02-16', name: 'Presidents\' Day', desc: '总统日' },
    { date: '2026-05-25', name: 'Memorial Day', desc: '阵亡将士纪念日' },
    { date: '2026-06-19', name: 'Juneteenth', desc: '六月节' },
    { date: '2026-07-04', name: 'Independence Day', desc: '独立日' },
    { date: '2026-09-07', name: 'Labor Day', desc: '劳动节' },
    { date: '2026-10-12', name: 'Columbus Day', desc: '哥伦布日' },
    { date: '2026-11-11', name: 'Veterans Day', desc: '退伍军人节' },
    { date: '2026-11-26', name: 'Thanksgiving Day', desc: '感恩节' },
    { date: '2026-12-25', name: 'Christmas Day', desc: '圣诞节' }
  ]
}

// 日本节假日
const JP_HOLIDAYS = {
  2026: [
    { date: '2026-01-01', name: '元日', desc: '新年' },
    { date: '2026-01-12', name: '成人の日', desc: '成人节' },
    { date: '2026-02-11', name: '建国記念の日', desc: '建国纪念日' },
    { date: '2026-02-23', name: '天皇誕生日', desc: '天皇诞生日' },
    { date: '2026-03-20', name: '春分の日', desc: '春分' },
    { date: '2026-04-29', name: '昭和の日', desc: '昭和日' },
    { date: '2026-05-03', name: '憲法記念日', desc: '宪法纪念日' },
    { date: '2026-05-04', name: 'みどりの日', desc: '绿之日' },
    { date: '2026-05-05', name: 'こどもの日', desc: '儿童节' },
    { date: '2026-07-20', name: '海の日', desc: '海之日' },
    { date: '2026-08-11', name: '山の日', desc: '山之日' },
    { date: '2026-09-21', name: '敬老の日', desc: '敬老日' },
    { date: '2026-09-23', name: '秋分の日', desc: '秋分' },
    { date: '2026-10-12', name: 'スポーツの日', desc: '体育日' },
    { date: '2026-11-03', name: '文化の日', desc: '文化日' },
    { date: '2026-11-23', name: '勤労感謝の日', desc: '勤劳感谢日' }
  ]
}

const ALL_HOLIDAYS = {
  CN: CHINA_HOLIDAYS,
  US: US_HOLIDAYS,
  JP: JP_HOLIDAYS
}

export default {
  name: 'HolidaysPage',
  data() {
    const now = new Date()
    return {
      selectedCountry: 'CN',
      selectedYear: now.getFullYear(),
      holidays: [],
      loading: false,
      countries: [
        { code: 'CN', flag: '🇨🇳', name: '中国' },
        { code: 'US', flag: '🇺🇸', name: '美国' },
        { code: 'JP', flag: '🇯🇵', name: '日本' }
      ]
    }
  },
  computed: {
    yearOptions() {
      const y = new Date().getFullYear()
      return [y, y + 1]
    }
  },
  mounted() {
    this.loadHolidays()
  },
  methods: {
    loadHolidays() {
      const data = ALL_HOLIDAYS[this.selectedCountry]
      if (data && data[this.selectedYear]) {
        this.holidays = data[this.selectedYear]
      } else {
        this.holidays = []
      }
    },
    formatMonth(dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return (d.getMonth() + 1) + '月'
    },
    formatDay(dateStr) {
      const d = new Date(dateStr + 'T00:00:00')
      return d.getDate()
    },
    isToday(dateStr) {
      const now = new Date()
      const pad = (n) => String(n).padStart(2, '0')
      const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
      return dateStr === today
    },
    isUpcoming(dateStr) {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      const d = new Date(dateStr + 'T00:00:00')
      const diff = (d - now) / 86400000
      return diff > 0 && diff <= 30
    },
    isPast(dateStr) {
      const now = new Date()
      now.setHours(0, 0, 0, 0)
      const d = new Date(dateStr + 'T00:00:00')
      return d < now
    }
  }
}
</script>

<style scoped>
.holidays-page { max-width: 640px; margin: 0 auto; padding: 20px 16px 30px; }

.page-header { text-align: center; padding: 24px 0 12px; }
.page-title { font-size: 26px; font-weight: 700; color: #1a1a2e; }
.page-desc { font-size: 14px; color: #888; margin-top: 6px; }

.select-row {
  display: flex; justify-content: center; gap: 16px; margin-bottom: 20px; flex-wrap: wrap;
}
.select-group { display: flex; flex-direction: column; gap: 6px; }
.select-group label { font-size: 13px; color: #888; font-weight: 500; }
.select-input {
  padding: 10px 14px; font-size: 15px; border: 2px solid #e0e0e0;
  border-radius: 8px; background: #fff; min-width: 160px;
}
.select-input.short { min-width: 100px; }
.select-input:focus { border-color: #1677ff; outline: none; }

.holiday-count {
  text-align: center; font-size: 13px; color: #999; margin-bottom: 12px;
}

.holiday-card {
  display: flex; align-items: center; gap: 14px;
  background: #fff; border-radius: 10px; padding: 14px 16px;
  margin-bottom: 8px; box-shadow: 0 1px 6px rgba(0,0,0,0.05);
  transition: transform 0.2s;
}
.holiday-card:hover { transform: translateX(4px); }
.holiday-card.is-past { opacity: 0.5; }

.h-date {
  display: flex; flex-direction: column; align-items: center;
  min-width: 50px; background: #f5f7fa; border-radius: 8px; padding: 6px 10px;
}
.h-month { font-size: 12px; color: #888; }
.h-day { font-size: 22px; font-weight: 700; color: #1a1a2e; }

.h-info { flex: 1; }
.h-name { font-size: 16px; font-weight: 600; color: #333; }
.h-desc { font-size: 12px; color: #999; margin-top: 2px; }

.h-badge {
  font-size: 11px; padding: 3px 8px; border-radius: 12px;
  background: #1677ff; color: #fff; white-space: nowrap;
}
.h-badge.upcoming { background: #fa8c16; }

.empty-hint { text-align: center; font-size: 14px; color: #bbb; padding: 40px 0; }

.help-block {
  margin-top: 30px; background: #fff; border-radius: 12px;
  padding: 20px 24px; box-shadow: 0 1px 6px rgba(0,0,0,0.05);
}
.help-block h3 { font-size: 15px; color: #333; margin-bottom: 10px; }
.help-block ul { list-style: disc; padding-left: 20px; }
.help-block li { font-size: 13px; color: #777; line-height: 2; }

@media (max-width: 480px) {
  .h-date { min-width: 40px; padding: 4px 8px; }
  .h-day { font-size: 18px; }
}
</style>
